<script setup lang="ts">
import { AllComponents } from "@/builder/client/AllComponents";
import ButtonComponent from "@/builder/components/button/ButtonComponent.vue";
import ConfirmationRenderContainer from "@/builder/components/form/ConfirmationRenderContainer.vue";
import FormComponent from "@/builder/components/form/FormComponent.vue";
import PromptRenderContainer from "@/builder/components/prompt/PromptRenderContainer.vue";
import { useRenderStore } from "@/builder/stores/renderStore";
import TestModeBanner from "@/components/shared/banner/TestModeBanner.vue";
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { computed, nextTick, ref } from "vue";
import { ComponentType, ModalDisplayType, PartialDisplayType } from "../../enums";
import TrustCenterContainer from "../trustCenter/TrustCenterContainer.vue";
import ErrorContainer from "../shared/ErrorContainer.vue";

//props
const props = defineProps(["showModal", "component", "organizationId", "showModalElements"]);
const renderStore = useRenderStore();

//refs
const showModalElements = ref(props.showModalElements);
const modalType = ref(renderStore.page.pageDesign.modalDisplay);
const modal = ref();
const form = ref();
const formStep = ref();
const isStepTooTall = ref(false);

const { activate, deactivate } = useFocusTrap(modal);

//computed
const getModalLayout = computed(() => {
    return {
        'rd-items-center rd-flex rd-modal-centered': modalType.value == ModalDisplayType.Lightbox,
        'rd-modal-slide-in': modalType.value == ModalDisplayType.Panel
    }
});

const getSubmitButtonPosition = computed(() => {
    return props.component.customData.buttons.submit.customData.alignment &&
        `rd-justify-${props.component.customData.buttons.submit.customData.alignment}`;
});

const getModalAnimation = computed(() => {
    let animationClasses = '';

    if (renderStore.showModal) {
        modalType.value == ModalDisplayType.Lightbox && (animationClasses = 'fade-in');
        modalType.value == ModalDisplayType.Panel && (animationClasses = 'slide-in');
    }
    else {
        modalType.value == ModalDisplayType.Lightbox && (animationClasses = 'fade-out');
        modalType.value == ModalDisplayType.Panel && (animationClasses = 'slide-out');
    }

    return animationClasses;
});

const currentStepTitle = computed(() => {
    const currentStep = renderStore.getFormStepByIndex(renderStore.step);
    if (!currentStep) return null;
    if (!currentStep.customData.displayTitle) return null;
    return currentStep.customData.title;
});

const getFormBackgroundColor = computed(() => {
    const formBgColor = props.component.styles.backgroundColor;
    return formBgColor ? formBgColor : '#FFFFFF';
});

const getScrollbarColor = computed(() => {
    const setColor = props.component.styles.backgroundColor;
    let scrollbarColor;

    if (setColor === '#878787' || setColor === 'rgb(135, 135, 135)') {
        scrollbarColor = '#FFFFFF #878787';
    }
    else if (!setColor) {
        scrollbarColor = '#878787 #FFFFFF';
    }
    else scrollbarColor = `#878787 ${setColor}`;

    return scrollbarColor;
});

//methods
const getFormStepHeight = (stepNumber: number) => {
    nextTick(() => {
        const stepHeight = formStep.value[stepNumber].children[0].offsetHeight,
            parentHeight = form.value.offsetHeight;

        stepHeight > parentHeight ?
            isStepTooTall.value = true :
            isStepTooTall.value = false;
    });
}

const closeModal = () => {
    if (!renderStore.isProcessing) {
        renderStore.showModal = false;
        document.body.classList.remove('rd-modal-open');

        setTimeout(() => {
            showModalElements.value = false;
            deactivate();
        }, 375);
    }
};

const loadModal = () => {
    renderStore.showModal = true;
    showModalElements.value = true;
    //getFormStepHeight(renderStore.step);

    setTimeout(() => {
        document.body.classList.add('rd-modal-open');
        activate()
    }, 425);
};
</script>

<template>
    <section class="modal-button-container rd-flex rd-w-full" :style="{
        justifyContent: component.customData.buttons.modal.customData.alignment
    }">
        <ButtonComponent :component="component.customData.buttons.modal" @click.stop="loadModal" />
    </section>

    <Teleport to="body">
        <div id="rd-modal-container" class="raise-donors">
            <div class="rd-h-full rd-left-0 rd-overflow-x-hidden rd-overflow-y-auto rd-fixed rd-top-0 rd-w-full rd-modal"
                id="rd-modal" tabindex="-1" role="dialog" aria-label="Giving Form Modal"
                :aria-hidden="showModalElements ? false : true" :class="showModalElements ? 'rd-block' : 'rd-hidden'"
                ref="modal" @click.stop="closeModal">

                <TestModeBanner v-if="renderStore.page.partialDisplayType === PartialDisplayType.Modal"
                    source="public" />

                <div @click.stop
                    class="rd-pointer-events-none rd-relative rd-w-auto rd-modal-wrapper rd-modal-scroll md:rd-modal-full"
                    :class="[getModalLayout, getModalAnimation]" role="document" ref="el">
                    <div class="rd-modal-content" :style="{ backgroundColor: component.styles.backgroundColor }">

                        <ErrorContainer />

                        <div class="rd-flex-auto rd-overflow-y-auto rd-relative rd-modal-body">

                            <section>
                                <!-- modal image -->
                                <div v-if="component.customData.modalImage" class="rd-modal-image"
                                    :style="{ 'background-image': 'url(' + component.customData.modalImage + ')' }">
                                </div>

                                <!-- modal components -->
                                <div class="rd-modal-form" :class="component.styles?.cssClass" :style="{
                                    backgroundColor: component.styles.backgroundColor,
                                    color: component.styles.fontColor
                                }">

                                    <header
                                        class="rd-items-center rd-flex rd-shrink-0 rd-modal-header rd-modal-header-gradient"
                                        :class="{ 'with-image': component.customData.modalImage }">
                                        <!-- prev button -->
                                        <template
                                            v-for="(item, index) in props.component.components.filter(x => x.type === ComponentType.FormStep)"
                                            :key="index">
                                            <template v-if="index === renderStore.step">
                                                <ButtonComponent v-if="renderStore.step > 0 && !renderStore.success"
                                                    aria-label="Go to previous step"
                                                    :component="item.customData.buttons.prev">
                                                </ButtonComponent>
                                            </template>
                                        </template>

                                        <template v-if="currentStepTitle && !renderStore.success">
                                            <div class="form-step-title">
                                                <span>{{ currentStepTitle }}</span>
                                            </div>
                                        </template>

                                        <button type="button" class="rd-btn-close" aria-label="Close"
                                            @click.stop="closeModal" @click="deactivate()">
                                            <i class="fa-solid fa-xmark"></i>
                                        </button>
                                    </header>

                                    <section class="rd-relative rd-form-body" :style="{
                                        backgroundColor: component.styles.backgroundColor
                                    }">
                                        <slot>
                                            <template v-if="!renderStore.success">
                                                <form name="rd-form" method="post"
                                                    :style="{ backgroundColor: component.styles.backgroundColor, 'scrollbar-color': getScrollbarColor }">
                                                    <FormComponent>
                                                        <template #children>
                                                            <template
                                                                v-for="(item, index) in props.component.components.filter(x => x.type === ComponentType.FormStep)"
                                                                :key="index">
                                                                <div class="rd-h-full"
                                                                    :class="index === renderStore.step ? 'rd-block' : 'rd-hidden'"
                                                                    ref="formStep">

                                                                    <component :is="AllComponents[item.type]"
                                                                        :component="item" />
                                                                </div>
                                                            </template>
                                                        </template>
                                                    </FormComponent>
                                                </form>
                                            </template>

                                            <template v-else>
                                                <template
                                                    v-if="props.component.components.filter(x => x.type === ComponentType.Confirmation).length > 0">
                                                    <ConfirmationRenderContainer
                                                        :component="props.component.components.filter(x => x.type === ComponentType.Confirmation)[0]"
                                                        :style="{ 'scrollbar-color': getScrollbarColor }" />
                                                </template>

                                                <template v-else>
                                                    <div class="form-step-container">
                                                        <div class="layout-row">
                                                            <div class="rd-w-full md:rd-flex-equal">
                                                                <h1>Thank you for your donation!</h1>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </template>
                                        </slot>
                                    </section>

                                    <template v-if="!renderStore.success">
                                        <footer class="rd-modal-footer rd-modal-footer-gradient"
                                            :class="{ 'with-image': component.customData.modalImage }">
                                            <template v-if="renderStore.maxSteps > 0">
                                                <!-- submit button -->
                                                <div v-if="renderStore.step === renderStore.maxSteps"
                                                    :class="['rd-flex rd-w-full', getSubmitButtonPosition]">

                                                    <ButtonComponent v-if="renderStore.step === renderStore.maxSteps"
                                                        :component="component.customData.buttons.submit"
                                                        :disabled="renderStore.hasErrors"
                                                        @click="renderStore.isProcessing">
                                                        <template #buttonText>
                                                            {{ component.customData.buttons.submit.customData.text }} {{
                                                                renderStore.submitButtonAmount }}
                                                        </template>
                                                    </ButtonComponent>
                                                </div>

                                                <!-- next buttons -->
                                                <template
                                                    v-for="(item, index) in props.component.components.filter(x => x.type === ComponentType.FormStep)"
                                                    :key="index">
                                                    <template v-if="index === renderStore.step">
                                                        <div class="rd-flex rd-w-full"
                                                            :class="item.customData.buttons.next.customData.alignment && `rd-justify-${item.customData.buttons.next.customData.alignment}`">

                                                            <ButtonComponent
                                                                v-if="renderStore.step !== renderStore.maxSteps"
                                                                :component="item.customData.buttons.next"
                                                                :disabled="renderStore.stepHasErrors(renderStore.step)" />
                                                        </div>
                                                    </template>
                                                </template>

                                            </template>
                                            <template v-else>
                                                <!-- submit button -->
                                                <div class="rd-flex rd-w-full" :class="getSubmitButtonPosition">
                                                    <ButtonComponent :component="component.customData.buttons.submit"
                                                        :disabled="renderStore.hasErrors"
                                                        @click="renderStore.isProcessing">
                                                        <template #buttonText>
                                                            {{ component.customData.buttons.submit.customData.text }} {{
                                                                renderStore.submitButtonAmount }}
                                                        </template>
                                                    </ButtonComponent>
                                                </div>
                                            </template>
                                        </footer>
                                    </template>
                                </div>
                            </section>
                        </div>

                        <!-- trust center -->
                        <div class="trust-center-modal" :style="{ backgroundColor: component.styles.backgroundColor }">
                            <TrustCenterContainer :page="renderStore.page" tooltip-container=".trust-center-modal" />
                        </div>

                        <PromptRenderContainer />
                    </div>
                </div>
            </div>

            <div class="rd-modal-backdrop"
                :class="[renderStore.showModal ? 'fade-in-backdrop' : 'fade-out-backdrop', { 'rd-hidden': !showModalElements }]">
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="scss">
.rd-modal-header-gradient,
.rd-modal-footer-gradient {
    background-color: v-bind(getFormBackgroundColor);
}

.rd-modal-header-gradient {
    box-shadow: -14px 8px 10px 0 v-bind(getFormBackgroundColor);

    &.with-image {
        box-shadow: 6px 18px 20px 0 v-bind(getFormBackgroundColor);
    }
}

.rd-modal-footer-gradient {
    box-shadow: -14px -8px 16px 0 v-bind(getFormBackgroundColor);

    &.with-image {
        box-shadow: 6px -8px 16px 0 v-bind(getFormBackgroundColor);
    }
}
</style>